import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABELS = {
  cgl: "Commercial General Liability",
  auto: "Automobile Liability",
  umbrella: "Umbrella / Excess Liability",
  wc: "Workers' Compensation",
  other: "Other coverage",
};
const LIMIT_FIELDS = {
  cgl: [["eachOccurrence", "Each occurrence"], ["generalAggregate", "General aggregate"]],
  auto: [["combinedSingleLimit", "Combined single limit"]],
  umbrella: [["eachOccurrence", "Each occurrence"], ["generalAggregate", "Aggregate"]],
  wc: [["elEachAccident", "E.L. each accident"], ["elDiseaseEaEmployee", "E.L. disease – each employee"], ["elDiseasePolicyLimit", "E.L. disease – policy limit"]],
  other: [],
};
const TYPES = ["cgl", "auto", "umbrella", "wc", "other"];

function blankCoverages() {
  const map = {};
  for (const type of TYPES) {
    map[type] = { required: false, other_name: "", additional_insured_required: false, waiver_required: false, limits: {} };
  }
  return map;
}

function fromTemplate(template) {
  const coverages = blankCoverages();
  for (const c of template?.coverages || []) {
    const type = c.type;
    if (!coverages[type]) continue;
    coverages[type] = {
      required: c.required !== false,
      other_name: c.other_name ?? c.otherTypeName ?? "",
      additional_insured_required: c.additional_insured_required ?? c.additionalInsuredRequired ?? false,
      waiver_required: c.waiver_required ?? c.waiverRequired ?? false,
      limits: Object.fromEntries(Object.entries(c.limits || {}).map(([k, v]) => [k, v == null ? "" : String(v)])),
    };
  }
  return {
    name: template?.name || "",
    certificate_holder_name: template?.certificateHolder?.expectedName || "",
    certificate_holder_address: template?.certificateHolder?.expectedAddress || "",
    min_days_remaining: template?.minDaysRemaining ?? 15,
    description_must_contain: (template?.descriptionMustContain || []).join(", "),
    coverages,
  };
}

export function TemplateEditorDialog({ open, onOpenChange, template, mode, canWrite, onSaved }) {
  const [form, setForm] = useState(fromTemplate(null));
  const [saving, setSaving] = useState(false);
  const readOnly = !canWrite;

  useEffect(() => {
    if (!open) return;
    const base = fromTemplate(template);
    if (mode === "clone") base.name = `${base.name} (copy)`;
    setForm(base);
  }, [open, template, mode]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setCoverage = (type, patch) => setForm((prev) => ({ ...prev, coverages: { ...prev.coverages, [type]: { ...prev.coverages[type], ...patch } } }));
  const setLimit = (type, key, value) => setForm((prev) => ({ ...prev, coverages: { ...prev.coverages, [type]: { ...prev.coverages[type], limits: { ...prev.coverages[type].limits, [key]: value } } } }));

  function buildPayload() {
    const coverages = TYPES.filter((type) => form.coverages[type].required).map((type) => {
      const c = form.coverages[type];
      const limits = {};
      for (const [key, value] of Object.entries(c.limits)) {
        const numeric = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
        if (!Number.isNaN(numeric)) limits[key] = numeric;
      }
      return {
        type,
        other_name: type === "other" ? (c.other_name || null) : null,
        required: true,
        additional_insured_required: !!c.additional_insured_required,
        waiver_required: !!c.waiver_required,
        limits,
      };
    });
    return {
      name: form.name.trim(),
      certificate_holder_name: form.certificate_holder_name.trim(),
      certificate_holder_address: form.certificate_holder_address.trim(),
      min_days_remaining: Number(form.min_days_remaining) || 0,
      description_must_contain: form.description_must_contain.split(",").map((s) => s.trim()).filter(Boolean),
      coverages,
    };
  }

  async function save(event) {
    event.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Give the template a name (at least 2 characters)."); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (mode === "edit") {
        const res = await api.patch(`/templates/${template.id}`, payload);
        const n = res.data?.reverified || 0;
        toast.success(n ? `Template updated. ${n} insured${n === 1 ? "" : "s"} re-verified.` : "Template updated.");
      } else {
        await api.post("/templates", payload);
        toast.success(mode === "clone" ? "Template cloned." : "Template created.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  }

  const title = mode === "edit" ? "Edit requirement template" : mode === "clone" ? "Clone requirement template" : mode === "view" ? "Requirement template" : "New requirement template";

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form className="space-y-6" data-testid="template-editor-form" onSubmit={save}>
          {readOnly && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="template-readonly-note">You have read-only access. Ask an owner or admin to change requirements.</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label htmlFor="tpl-name">Template name</Label><Input data-testid="template-name-input" disabled={readOnly} id="tpl-name" onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Electrical contractors" value={form.name} /></div>
            <div><Label htmlFor="tpl-holder">Certificate holder name</Label><Input data-testid="template-holder-name-input" disabled={readOnly} id="tpl-holder" onChange={(e) => setField("certificate_holder_name", e.target.value)} placeholder="Your legal entity name" value={form.certificate_holder_name} /></div>
            <div><Label htmlFor="tpl-days">Minimum days before expiration</Label><Input data-testid="template-min-days-input" disabled={readOnly} id="tpl-days" onChange={(e) => setField("min_days_remaining", e.target.value)} type="number" value={form.min_days_remaining} /></div>
            <div className="sm:col-span-2"><Label htmlFor="tpl-addr">Certificate holder address</Label><Input data-testid="template-holder-address-input" disabled={readOnly} id="tpl-addr" onChange={(e) => setField("certificate_holder_address", e.target.value)} placeholder="Street, City, State ZIP" value={form.certificate_holder_address} /></div>
            <div className="sm:col-span-2"><Label htmlFor="tpl-desc">Description must contain (comma-separated)</Label><Textarea data-testid="template-description-input" disabled={readOnly} id="tpl-desc" onChange={(e) => setField("description_must_contain", e.target.value)} placeholder="e.g. additional insured, waiver of subrogation" rows={2} value={form.description_must_contain} /></div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900">Required coverages</h3>
            <p className="text-xs text-slate-500">Enable each coverage this group must carry and set the minimum limits.</p>
            <div className="mt-3 space-y-3">
              {TYPES.map((type) => {
                const c = form.coverages[type];
                return (
                  <div className={`rounded-lg border p-4 ${c.required ? "border-blue-200 bg-blue-50/40" : "border-slate-200"}`} data-testid={`coverage-row-${type}`} key={type}>
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        <Checkbox checked={c.required} data-testid={`coverage-required-${type}`} disabled={readOnly} onCheckedChange={(v) => setCoverage(type, { required: !!v })} />
                        {TYPE_LABELS[type]}
                      </label>
                    </div>
                    {c.required && (
                      <div className="mt-4 space-y-3 pl-6">
                        {type === "other" && <div><Label>Coverage name</Label><Input data-testid="coverage-other-name" disabled={readOnly} onChange={(e) => setCoverage(type, { other_name: e.target.value })} placeholder="e.g. Professional Liability" value={c.other_name} /></div>}
                        {LIMIT_FIELDS[type].length > 0 && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            {LIMIT_FIELDS[type].map(([key, label]) => (
                              <div key={key}><Label>{label} (minimum $)</Label><Input data-testid={`coverage-limit-${type}-${key}`} disabled={readOnly} inputMode="numeric" onChange={(e) => setLimit(type, key, e.target.value)} placeholder="1000000" value={c.limits[key] || ""} /></div>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={c.additional_insured_required} data-testid={`coverage-ai-${type}`} disabled={readOnly} onCheckedChange={(v) => setCoverage(type, { additional_insured_required: !!v })} />Additional insured required</label>
                          <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={c.waiver_required} data-testid={`coverage-waiver-${type}`} disabled={readOnly} onCheckedChange={(v) => setCoverage(type, { waiver_required: !!v })} />Waiver of subrogation required</label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!readOnly && (
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} type="button" variant="outline">Cancel</Button>
              <Button data-testid="template-editor-save" disabled={saving} type="submit">{saving ? "Saving…" : mode === "edit" ? "Save changes" : mode === "clone" ? "Create copy" : "Create template"}</Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
